import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	EventEmitter,
	inject,
	model,
	OnInit
} from '@angular/core';
import { ManualDippingResultsTableService } from './manual-dipping-results-table.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MDTask, MDTaskResult } from '@helpers/types/models';
import {
	AlarisEditPanelService,
	AlarisFilterManagerComponent,
	EditPanelWidth,
	LocalTableUtils,
	PageEvent,
	TableFiltersIndicator,
	TableRefBooksIndicator,
	TableSimple,
	TableSortIndicator,
	TableWithFilter,
	TableWithSorting
} from '@campaign-portal/components-library';
import { 
	ManualDippingResultsTableFieldsService, 
	MDRESULTS_TABLE_FILTERS, 
	MDRESULTS_TABLE_SORTING 
} from './manual-dipping-results-table.fields.service';
import { BehaviorSubject, finalize } from 'rxjs';
import { FilterType, SortDirection } from '@campaign-portal/namespace/common/rpc.params';

@Component({
	selector: 'app-manual-dipping-results-table',
	standalone: false,
	templateUrl: './manual-dipping-results-table.component.html',
	styleUrls: [
		'./manual-dipping-results-table.component.scss',
		// eslint-disable-next-line max-len
		'../../../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss'
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManualDippingResultsTableComponent 
implements OnInit, TableSimple<MDTaskResult>, TableWithFilter, TableWithSorting {
	readonly exporting$ = new BehaviorSubject<boolean>(false);

	mdTaskID!: number | null;
	mdTask!: MDTask | undefined;

	tHeaders = model(LocalTableUtils.toTableFields(this.fieldsService.headers));
	tRows: MDTaskResult[] = [];
	sorting = model<TableSortIndicator>(MDRESULTS_TABLE_SORTING);
	filters = model<TableFiltersIndicator>(MDRESULTS_TABLE_FILTERS);
	readonly tFiltersChange = new EventEmitter<TableFiltersIndicator>();

	total = 0;
	activePage = 0;
	pageSize = 25;

	readonly pageSizeOptions = [25, 50, 100];

	private readonly keyObj: Record<string, string> = {
		number: 'number',
		mccmnc: 'mccmnc',
		ported: 'ported',
		sourceName: 'source_name'
	}

	private destroyRef = inject(DestroyRef);

	constructor(
		readonly service: ManualDippingResultsTableService,
		private route: ActivatedRoute,
		private readonly fieldsService: ManualDippingResultsTableFieldsService,
		private cdRef: ChangeDetectorRef,
		private editPanel: AlarisEditPanelService
	) { }

	ngOnInit(): void {
		this.mdTaskID = Number(this.route.snapshot.paramMap.get('id'));
		this.resetFilters();
		this.load();
		this.service.loadTask(this.mdTaskID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
			this.mdTask = res;
			this.setRefBooksInFields({
				sourceName: { list$: new BehaviorSubject<string[]>(res.sources) }
			});
			this.cdRef.markForCheck();
		})
	}

	confirm(): void {
		if (this.mdTask && this.mdTask.mdTaskID !== null) {
			this.exporting$.next(true);
			this.service
				.export(this.mdTask.mdTaskID)
				.pipe(
					takeUntilDestroyed(this.destroyRef),
					finalize(() => this.exporting$.next(false))
				)
				.subscribe();
		}
	}

	resetFilters(): void {
		this.service.filters = [];
		this.service.sorting = structuredClone(this.service.sortingDefault);
		this.sorting.set(
			new Map()
				.set('number', { enabled: true, value: SortDirection.EMPTY })
		);
		this.filters.set(
			new Map()
				.set('number', { enabled: true, value: undefined, filterType: FilterType.LIKE })
				.set('mccmnc', { enabled: true, value: undefined, filterType: FilterType.LIKE })
				.set('ported', { enabled: true, value: undefined, filterType: FilterType.LIKE })
				.set('sourceName', { enabled: true, value: undefined, filterType: FilterType.LIKE })
		);
	}

	load(): void {
		this.service.pageParams.limit = this.pageSize;
		this.service.pageParams.activePage = this.activePage;
		if (this.mdTaskID !== null && this.mdTaskID !== undefined) {
			this.service.loadResult(this.mdTaskID)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(res => {
					if (res.data && res.length) {
						this.total = res.length;
						this.tRows = res.data;
						this.cdRef.markForCheck();
					}
				})
		}
	}

	paginationChange(event: PageEvent): void {
		this.pageSize = event.pageSize;
		this.activePage = event.pageIndex;
		this.saveState();
		this.load();
	}

	applyFilter($event: TableFiltersIndicator): void {
		this.service.filters = [];
		const booleanFields = [
			'ported'
		];
		$event.forEach((value, key) => {
			if (value.value !== undefined && key && value.value !== null) {
				this.service.filters.push({
					key: this.keyObj[key] || key,
					value: booleanFields.includes(key) ? 
						this.valueToBoolean(value.value)
						: 
						value.value 
				})
			}
		});
		this.load();
	}

	applySorting($event: TableSortIndicator): void {
		$event.forEach(
			(sort, variable) => {
				if (sort?.enabled && sort?.value) {
					this.service.sorting.active = this.keyObj[variable] || variable;
					this.service.sorting.direction = sort.value.toLowerCase();
				} else {
					this.service.sorting = structuredClone(this.service.sortingDefault);
				}
			}
		);
		this.load();
	}

	editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.SM, {
			tHeaders: this.tHeaders(),
			filters: this.filters,
			filtersChange: this.tFiltersChange,
		});
	}


	get isFiltersApplied(): boolean {
		return this.service.filters.length > 0;
	}

	private saveState(): void {
		this.service.pageParams.limit = this.pageSize;
	}

	private valueToBoolean(value: any): string | null {
		if (value === 'No') {
			return '0';
		}
		if (value === 'Yes') {
			return '1';
		}
		return null;
	}

	private setRefBooksInFields(refBooks: TableRefBooksIndicator): void {
		this.tHeaders().forEach((field) => {
			const refBook = refBooks[field.variable];
			if (refBook) {
				Object.assign(field, { data$: refBook.list$, loading$: refBook.loading$ });
			}
		});
	}
}
